/**
 * Oil Data Hook
 * 
 * Custom hook for managing oil data including loading,
 * error handling, and automatic data fetching on parameter changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { OilResponse, OilSample, DateRange } from '../services/equipment/types';
import { useDateRange } from './use-date-range';
import { oilService } from '../services/equipment/oil-service';

interface UseOilDataOptions {
  /** Site identifier */
  site?: string;
  /** Asset identifier */
  asset?: string;
  /** Position identifier (may include equipment prefix) */
  position?: string;
}

interface UseOilDataReturn {
  /** Recommendation data */
  data: OilResponse | null;
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Fetch recommendation data function */
  fetchOilSamples: (site: string, asset: string, position: string, targetDateRange: DateRange) => Promise<void>;
  /** Retry the last failed request */
  retry: () => Promise<void>;
  /** Clear data and reset state */
  clear: () => void;
}

/**
 * Hook for managing ultrasound baseline recommendation data
 */
export const useOilData = (
  options: UseOilDataOptions
): UseOilDataReturn => {
  const {
    site,
    asset,
    position
  } = options;

  const { dateRange: contextDateRange } = useDateRange();

  const [data, setData] = useState<OilResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSite, setCurrentSite] = useState<string | null>(null);
  const [currentAsset, setCurrentAsset] = useState<string | null>(null);
  const [currentPosition, setCurrentPosition] = useState<string | null>(null);
  const [currentDateRange, setCurrentDateRange] = useState<DateRange | null>(contextDateRange);

  /**
   * Fetch oil data
   */
  const fetchOilSamples = useCallback(async (
    targetSite: string,
    targetAsset: string,
    targetPosition: string,
    targetDateRange: DateRange
  ) => {
    if (!targetSite || !targetAsset || !targetPosition) return;

    setIsLoading(true);
    setError(null);

    try {
      const recommendationData = await oilService.getOilSamples(
        targetSite,
        targetAsset,
        targetPosition,
        targetDateRange.startDate,
        targetDateRange.endDate
      );
      
      setData(recommendationData);
      setCurrentSite(targetSite);
      setCurrentAsset(targetAsset);
      setCurrentPosition(targetPosition);
      setCurrentDateRange(targetDateRange);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      }  
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Retry the last failed request
   */
  const retry = useCallback(async () => {
    if (!currentSite || !currentAsset || !currentPosition || !currentDateRange) return;
    await fetchOilSamples(currentSite, currentAsset, currentPosition, currentDateRange);
  }, [currentSite, currentAsset, currentPosition, currentDateRange, fetchOilSamples]);

  /**
   * Clear data and reset state
   */
  const clear = useCallback(() => {
    setData(null);
    setError(null);
    setCurrentSite(null);
    setCurrentAsset(null);
    setCurrentPosition(null);
    setCurrentDateRange(null);
  }, []);

  // Auto-fetch when parameters change - always fetch when valid parameters are provided
  useEffect(() => {
    // Always fetch data when all parameters are provided and any parameter has changed
    if (site && asset && position && contextDateRange) {
      if (site !== currentSite || asset !== currentAsset || position !== currentPosition || contextDateRange != currentDateRange) {
        fetchOilSamples(site, asset, position, contextDateRange);
      }
    }
  }, [site, asset, position, currentSite, currentAsset, currentPosition, contextDateRange, currentDateRange, fetchOilSamples]);

  // Clear data when parameters are removed
  useEffect(() => {
    if (
      (!site && currentSite) || 
      (!asset && currentAsset) || 
      (!position && currentPosition) ||
      (!contextDateRange && currentDateRange)
    ) {
      clear();
    }
  }, [site, currentSite, asset, currentAsset, position, currentPosition, contextDateRange, currentDateRange, clear]);

  return {
    data,
    isLoading,
    error,
    fetchOilSamples,
    retry,
    clear
  };
};